import { z } from 'zod';
import type {
  CreateCoverShipmentRequest,
  CreateCoverShipmentResponse,
} from './create-cover-shipment.dto.js';
import {
  ShipmentRangeBuilderInputSchema,
  ShipmentSourceEvidenceSchema,
  ShipmentTargetBuilderInputSchema,
  ShipmentTotalsSummarySchema,
  validateShipmentSourceEvidencePair,
} from '../shipments/shipments-shared.schema.js';
import {
  CoverShipmentDetailSchema,
  CoverShipmentReceiverSelectionSchema,
} from './cover-shipments-shared.schema.js';
import { CoverSourcePreparationRequestDetailSchema } from '../source-preparation/source-preparation.schema.js';

const baseShape = {
  companyId: z.uuid(),
  stationId: z.uuid(),
  receiver: CoverShipmentReceiverSelectionSchema,
  attachmentReceiptIds: z.array(z.uuid()).nonempty(),
  sourcePreparationRequestId: z.uuid().nullable().optional(),
  sourcePreparationSnapshot: CoverSourcePreparationRequestDetailSchema.nullable().optional(),
  ...ShipmentSourceEvidenceSchema.shape,
};

const CreateCoverShipmentRangeSchema = ShipmentRangeBuilderInputSchema.extend(baseShape).strict();
const CreateCoverShipmentTargetSchema = ShipmentTargetBuilderInputSchema.extend(baseShape).strict();

export const CreateCoverShipmentRequestSchema: z.ZodType<CreateCoverShipmentRequest> = z
  .discriminatedUnion('mode', [CreateCoverShipmentRangeSchema, CreateCoverShipmentTargetSchema])
  .superRefine((value, ctx) => {
    const rangeStartMs = Date.parse(value.rangeStartAt);
    const rangeEndMs = Date.parse(value.rangeEndAt);
    if (!Number.isNaN(rangeStartMs) && !Number.isNaN(rangeEndMs) && rangeStartMs >= rangeEndMs) {
      ctx.addIssue({
        code: 'custom',
        message: 'rangeStartAt must be before rangeEndAt.',
        path: ['rangeEndAt'],
      });
    }
    validateShipmentSourceEvidencePair(value, ctx);
  });

export const CreateCoverShipmentResponseSchema: z.ZodType<CreateCoverShipmentResponse> = z
  .object({
    coverShipment: CoverShipmentDetailSchema,
    lineItemIds: z.array(z.uuid()),
    purchaseCommittedIds: z.array(z.uuid()),
    totals: ShipmentTotalsSummarySchema,
  })
  .strict();
